import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/lib/seo";
import { waLink } from "@/lib/whatsapp";
import { getService, SERVICES } from "@/data/services";
import { Reveal } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";

const ServicePage = () => {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6" data-testid="service-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-900">Servicio no encontrado</h1>
        <p className="mt-4 text-slate-600">El servicio que buscas no existe o fue movido.</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white hover:bg-brand-dark" data-testid="service-not-found-home-link">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-page-${slug}`}>
      <SEO
        title={`${service.name} en Medellín | Colors Limpieza`}
        description={`${service.tagline}. Servicio a domicilio en Medellín con inyección-succión, secado rápido e insumos biodegradables. Cotiza gratis por WhatsApp.`}
      />

      {/* HERO SERVICIO */}
      <section className="relative overflow-hidden bg-white" data-testid="service-hero">
        <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-brand-light opacity-60 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <nav className="text-xs text-slate-400" data-testid="service-breadcrumb">
              <Link to="/" className="hover:text-brand">Inicio</Link>
              <span className="mx-2">/</span>
              <span>Servicios</span>
              <span className="mx-2">/</span>
              <span className="text-brand-dark">{service.name}</span>
            </nav>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              {service.name} en <span className="text-brand">Medellín</span>
            </h1>
            <p className="mt-4 text-lg font-medium text-sunset-dark">{service.tagline}</p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">{service.description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={waLink(service.waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-sunset px-7 py-4 text-sm font-bold text-white shadow-[0_8px_25px_-5px_rgba(255,162,89,0.4)] transition-colors hover:bg-sunset-dark"
                data-testid="service-hero-whatsapp-button"
              >
                <MessageCircle size={18} /> Cotizar este servicio
              </a>
              <a
                href="#cotizar-servicio"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand bg-white px-7 py-4 text-sm font-bold text-brand transition-colors hover:bg-brand hover:text-white"
                data-testid="service-hero-form-button"
              >
                Llenar formulario
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={service.image}
              alt={service.name}
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-[0_10px_30px_-10px_rgba(0,183,205,0.25)]"
              data-testid="service-hero-image"
            />
          </Reveal>
        </div>
      </section>

      {/* INCLUYE */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="service-includes-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <img
              src={service.galleryImage}
              alt={`${service.name} - detalle`}
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg"
              loading="lazy"
              data-testid="service-gallery-image"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Qué incluye</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Un servicio completo, sin sorpresas
            </h2>
            <ul className="mt-8 space-y-4">
              {service.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm" data-testid={`service-include-item`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-light">
                    <Check size={14} className="text-brand-dark" />
                  </span>
                  <span className="text-sm font-medium leading-relaxed text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="bg-white py-16 md:py-24" data-testid="service-benefits-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Beneficios que vas a notar
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {service.benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-slate-100 bg-white p-6 transition-all duration-300 hover:border-brand/30 hover:shadow-[0_20px_40px_-15px_rgba(15,23,42,0.08)] md:p-7" data-testid={`service-benefit-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sunset-light">
                    <Sparkles size={20} className="text-sunset-dark" />
                  </div>
                  <p className="mt-4 text-sm font-medium leading-relaxed text-slate-700">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="service-faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <h2 className="text-center font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Preguntas frecuentes
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 space-y-3">
              {service.faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  className="rounded-2xl border border-slate-100 bg-white px-6 shadow-sm"
                  data-testid={`service-faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-display text-sm font-semibold text-slate-900 hover:text-brand sm:text-base">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* COTIZADOR */}
      <section id="cotizar-servicio" className="bg-white py-16 md:py-24" data-testid="service-quote-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-brand to-brand-dark p-8 md:p-12">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Cotiza tu {service.shortName.toLowerCase() === "vehículos" ? "limpieza de vehículo" : `limpieza de ${service.shortName.toLowerCase()}`} ahora
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-white/85">
                  Dinos qué necesitas y te enviamos el precio exacto por WhatsApp en minutos. Atendemos todos los días en Medellín.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="rounded-3xl bg-white p-6 shadow-2xl md:p-8">
                  <QuoteForm defaultService={slug} />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* OTROS SERVICIOS */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="service-others-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              También te puede interesar
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2 md:gap-8">
            {others.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.1}>
                <Link
                  to={`/servicios/${s.slug}`}
                  className="group flex items-center gap-6 rounded-3xl bg-white p-5 shadow-[0_10px_30px_-10px_rgba(0,183,205,0.12)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(15,23,42,0.15)]"
                  data-testid={`service-other-${s.slug}`}
                >
                  <img src={s.image} alt={s.name} className="h-24 w-24 shrink-0 rounded-2xl object-cover sm:h-28 sm:w-28" loading="lazy" />
                  <div>
                    <h3 className="font-display text-lg font-semibold text-slate-900">{s.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{s.tagline}</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand group-hover:text-brand-dark">
                      Ver servicio <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
